import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { Subject } from "rxjs";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ButtonComponent } from "../../../../button/button.component";
import { IMapComponent, RightClickData } from "../../../map.models";
import { IMapGeometry, IMapGeometryPoint, IMapSettings } from "../../../map.settings";
import { MapEditService } from "../../../services/map-edit.service";
import { SpatialService } from "../../../services/spatial.service";
import { EditMenuComponent } from "../edit-menu/edit-menu.component";
import { GeometryInformation } from "../edit-menu/models/edit-models";

@Component({
    selector: "rask-right-click-popup",
    templateUrl: "./right-click-popup.component.html",
    styleUrls: ["./right-click-popup.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        NgbTooltip,
        EditMenuComponent
    ]
})
export class RightClickPopupComponent implements OnInit, OnDestroy {
    protected geometries: GeometryInformation[] = [];
    protected map: IMapComponent;
    protected mapsettings: IMapSettings;
    protected point: IMapGeometryPoint;
    protected queryingLayers: boolean;
    protected x: number;
    protected y: number;

    private readonly mapEditService = inject(MapEditService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly spatialService = inject(SpatialService);

    private rightClickData: RightClickData;

    get data(): RightClickData {
        return this.rightClickData;
    }

    set data(value: RightClickData) {
        this.rightClickData = value;
        this.geometries = value.hitlist;
        this.setPoint(value.clicked);
    }

    ngOnDestroy(): void {
        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    ngOnInit(): void {
        this.mapsettings = this.map.getMapSettings();
    }

    protected canPaste(): boolean {
        return this.mapEditService.canPasteFromWindowsClipboard();
    }

    protected copy(): void {
        this.mapEditService.copyToWindowsClipboard([new GeometryInformation(this.point, null, null)]);
        this.map?.clearPreview();
        this.close();
    }

    protected navigate(): void {
        this.mapEditService.pasteFromWindowsClipboard().then(clipboardItem => {
            if (clipboardItem.valid) {
                const clipboardPoint = this.spatialService.toRask({ geometry: { x: clipboardItem.x,
                    y: clipboardItem.y } as unknown as Point } as unknown as Graphic, false);
                this.map?.gotoGeometries([clipboardPoint], true);
                this.map?.clearPreview();
                this.close();
            }
        });
    }

    private close(): void {
        this.map?.closePopups();
    }

    private getAsMapGeometryPoint(point: IMapGeometry): IMapGeometryPoint {
        if ((point as any).x) {
            // IMapGeometryPoint
            return point as IMapGeometryPoint;
        } else if ((point as any).wkt) {
            // IMapGeometryWicket
            const esriGeometry = this.spatialService.convertToGraphic(point);
            return this.spatialService.toRaskPoint({ geometry: esriGeometry } as any);
        }
        return null;
    }

    private setPoint(point: IMapGeometry): void {
        if (ObjectUtils.hasValue(point)) {
            this.point = this.getAsMapGeometryPoint(point);
            this.x = ObjectUtils.hasValue(this.point?.easting) ? Math.floor(this.point.easting) : 0;
            this.y = ObjectUtils.hasValue (this.point?.northing) ? Math.floor(this.point.northing) : 0;
        }
    }
}
